// Mirrors doc-review-api's underwriting DTOs (UnderwritingDocumentListResponse / UnderwritingDocumentResponse).

export enum UnclassifiedDocumentStatus {
  /** Waiting for a human Save (whether the AI is still classifying is the list's anyProcessing). */
  PENDING = 'PENDING',
  SAVED = 'SAVED',
}

export interface UnclassifiedDocument {
  id: number;
  fileName: string;
  /** LocalDateTime — doc-review-service serializes it as [y, M, d, h, m, s, nanos]; ISO string also accepted. */
  uploadedDate: number[] | string;
  uploadedBy: string;
  status: UnclassifiedDocumentStatus;
  /** Document type suggested by the AI; null when there was no match. */
  taskCode: string | null;
  /** Destination task suggested by the AI — used to prefill the Section and Task dropdowns. */
  taskId: number | null;
}

export interface UnclassifiedDocumentListResponse {
  pendingCount: number;
  /** The AI is still classifying documents of this loan — the table is locked. */
  anyProcessing: boolean;
  /** Only PENDING rows — SAVED ones are filtered out by the backend. */
  documents: UnclassifiedDocument[];
  /** Task codes allowed in the Task dropdown; empty if the whitelist couldn't be read. */
  allowedTaskCodes: string[];
}

/** The fields this screen uses from a getLoanTasks (TaskV2GridResponse) task. */
export interface LoanTask {
  taskId: number;
  title: string;
  taskCode: string;
  /** Parent tasks only group their children — they are not real destinations. */
  parentTask: boolean | null;
  /** Section category, e.g. SUBJECT_PROPERTY — shared by every task of the section. */
  category: string;
  /** PROPERTY_ID / BORROWER_ID / … — which property or borrower the section is about, if any. */
  type2: string | null;
  typeId2: number | null;
}

/** "Create new task…" in the Task dropdown — never a real taskId. */
export const NEW_TASK_OPTION = 'NEW_TASK';

/**
 * "Addendum for Purchase Contract" in the Task dropdown (25-oct-2026, Fernando's ticket
 * "Unclassified Docs - Addendum is not handled for manual upload") — never a real taskId either;
 * picking it still saves to the Purchase Contract task's own taskId, with isAddendum: true.
 */
export const ADDENDUM_TASK_OPTION = 'ADDENDUM_FOR_PURCHASE_CONTRACT';
export const ADDENDUM_TASK_TITLE = 'Addendum for Purchase Contract';
/**
 * The only task code this applies to — must match roc-services'
 * UnderwritingDocConstants.PURCHASE_CONTRACT_ADDENDUM_TASK_CODE
 * (TaskV2Constants.PURCHASE_CONTRACT_ALL_ADDENDUMS) exactly. Deliberately not the other 2 task
 * codes UnderwritingDocType.PURCHASE_CONTRACT also resolves ("Purchase Contract", "Purchase And
 * Sale Agreement (PSA) with all addendums") — neither is in the auto-upload whitelist, and
 * PurchaseContractReviewDialog (the addendum upload's own screen) only ever opens for this one.
 */
export const PURCHASE_CONTRACT_ADDENDUM_TASK_CODE = 'Purchase Contract with all Addendums';

/** What the new-task dialog asks for; the section (category/type2/typeId2) comes from the row. */
export interface NewTaskFormValues {
  title: string;
}

/** One getLoanTasks section: its display name (property/borrower sections include the address/name) and its tasks. */
export interface LoanTaskSection {
  name: string;
  tasks: LoanTask[];
}

// Mirrors UnderwritingSaveRequest / UnderwritingSaveResponse (POST /{id}/save).

export interface UnclassifiedDocumentSaveRequest {
  taskId: number;
  /** Only true when "Addendum for Purchase Contract" was picked — omitted (not false) otherwise. */
  isAddendum?: boolean;
}

export interface UnclassifiedDocumentSaveResponse {
  status: string;
  targetTaskStatus: string | null;
}
